import axios from 'axios'
import { useAdminStore } from './stores/admin'
import { useAuthStore } from './stores/auth'
import { useSharedStore } from './stores/shared'
import { useNotificationStore } from './stores/notification'

export default class Api {
  private static readonly MODE = (import.meta.env.VITE_APP_ENV || 'dev').toLowerCase()

  public static readonly HOST =
    Api.MODE === 'prod'
      ? 'https://noisetag.norsonic.com'
      : Api.MODE === 'test'
        ? 'https://noisetagtest.norsonic.com'
        : 'http://localhost'
  public static readonly API_URL = this.HOST + '/api'
  public static readonly AUTH_URL = this.API_URL + '/auth'

  private static get _projectName() {
    return useSharedStore().projectName
  }
  private static get _projectOrigin() {
    return useSharedStore().projectOrigin
  }

  private static readonly _axiosInstance = axios.create({
    baseURL: `${Api.API_URL}/`,
    withCredentials: true
  })

  private static isSilentAuthCheck(error: any): boolean {
    return error?.config?.params?.silent_auth_check === 1
  }

  static {
    // Add response error interceptor
    Api._axiosInstance.interceptors.response.use(
      (response) => response,
      (error) => {
        const notificationStore = useNotificationStore()
        const status = error.response?.status
        const data = error.response?.data
        const isSilentAuthCheck = Api.isSilentAuthCheck(error)

        if (status === 401) {
          if (!isSilentAuthCheck) {
            notificationStore.error(
              'Unauthorized',
              'Your session has expired. Please log in again.',
              status
            )
          }
        } else if (status === 403) {
          notificationStore.error(
            'Access Denied',
            'You do not have permission to access this resource.',
            status
          )
        } else if (status === 404) {
          notificationStore.error('Not Found', 'The requested resource could not be found.', status)
        } else if (status && status >= 500) {
          notificationStore.error(
            'Server Error',
            data?.detail || 'An unexpected server error occurred. Please try again later.',
            status
          )
        } else if (error.message === 'Network Error') {
          notificationStore.error(
            'Network Error',
            'Unable to connect to the server. Please check your internet connection.'
          )
        }

        return Promise.reject(error)
      }
    )
  }

  public static get axiosInstance() {
    return Api._axiosInstance
  }

  public static get projectParams() {
    if (!Api._projectName) return null
    if (!Api._projectOrigin) return null
    return {
      project_name: Api._projectName,
      project_origin: Api._projectOrigin
    }
  }

  public static async getAuth(options?: { silent?: boolean }) {
    const config = options?.silent
      ? {
          params: {
            silent_auth_check: 1
          }
        }
      : undefined

    const response = await Api._axiosInstance.get(`auth/`, config)
    if (response.status === 200) useAuthStore().setUser(response.data)
  }

  public static async impersonate(username: string) {
    const response = await Api._axiosInstance.post(`auth/impersonate`, { username })
    if (response.status === 200) useAuthStore().setUser(response.data)
  }

  public static async impersonateAccess(role: number, projectIds?: Array<number | string>) {
    const payload: { role: number; project_ids?: number[] } = { role }
    if (projectIds && projectIds.length) {
      payload.project_ids = projectIds.map((id) => Number(id)).filter((id) => !Number.isNaN(id))
    }
    const response = await Api._axiosInstance.post(`auth/impersonate/access`, payload)
    if (response.status === 200) useAuthStore().setUser(response.data)
  }

  public static async stopImpersonation() {
    const response = await Api._axiosInstance.post(`auth/impersonate/stop`)
    if (response.status === 200) useAuthStore().setUser(response.data)
  }

  public static async getTaxonomy() {
    if (!Api.projectParams) return
    const response = await Api._axiosInstance.get(`admin/taxonomy`, { params: Api.projectParams })
    if (response.status === 200) useAdminStore().setTaxonomy(response.data)
  }

  public static async putTaxonomy(id: string | number, dbRef: string, name: string) {
    await Api._axiosInstance.put(
      `admin/taxonomy`,
      {
        id: id,
        name: name
      },
      {
        params: Api.projectParams
      }
    )
  }

  public static async deleteTaxonomy(id: number) {
    await Api._axiosInstance.delete(`admin/taxonomy`, {
      params: { ...Api.projectParams, id: id }
    })
  }

  public static async getProjects() {
    const response = await Api._axiosInstance.get(`admin/projects`, {
      params: { project_origin: Api._projectOrigin }
    })
    if (response.status === 200) useAdminStore().setProjects(response.data)
  }

  public static async postProject(name: string) {
    const response = await Api._axiosInstance.post(`admin/projects`, {
      name: name,
      origin: Api._projectOrigin?.toLowerCase()
    })
    if (response.status === 200) useAdminStore().setProjects(response.data)
  }

  public static async getStatistics(from: string, to: string) {
    if (!Api.projectParams) return
    const response = await Api._axiosInstance.get('admin/statistics', {
      params: { from_date: from, to_date: to, ...Api.projectParams }
    })
    if (response.status === 200) useAdminStore().setAdminStatistics(response.data)
  }

  public static async getFeedback(limit = 100, offset = 0, append = false) {
    if (!Api.projectParams) return
    const response = await Api._axiosInstance.get(`admin/feedback`, {
      params: { ...Api.projectParams, limit, offset }
    })
    if (response.status === 200) useAdminStore().setFeedback(response.data, append)
  }

  public static async deleteFeedback(id: number) {
    await Api._axiosInstance.delete(`admin/feedback/${id}`, { params: Api.projectParams })
  }

  public static async putFeedback(id: number, dbRef: string, value: any) {
    await Api._axiosInstance.put(
      `admin/feedback/${id}/${dbRef}`,
      {
        data: value
      },
      {
        params: Api.projectParams
      }
    )
  }

  public static async bulkApproveFeedback(ids: number[]) {
    const response = await Api._axiosInstance.put(`admin/feedbacks`, ids, {
      params: Api.projectParams
    })
    return response.data
  }

  public static async getAnnotation(showResolved = false, limit = 100, offset = 0, append = false) {
    if (!Api.projectParams) return
    const response = await Api._axiosInstance.get(`admin/annotation`, {
      params: { ...Api.projectParams, resolved: showResolved, limit, offset }
    })
    if (response.status === 200) useAdminStore().setAnnotation(response.data, append)
  }

  public static async getAnnotationResolvedCounts() {
    if (!Api.projectParams) return
    const response = await Api._axiosInstance.get(`admin/annotation/resolved-counts`, {
      params: Api.projectParams
    })
    if (response.status === 200) {
      useAdminStore().setAnnotationResolvedCounts(response.data || {})
    }
  }

  public static async deleteAnnotation(id: number) {
    await Api._axiosInstance.delete(`admin/annotation/${id}`, { params: Api.projectParams })
  }

  public static async putAnnotation(id: number, dbRef: string, value: any) {
    await Api._axiosInstance.put(
      `admin/annotation/${id}/${dbRef}`,
      {
        data: value
      },
      {
        params: Api.projectParams
      }
    )
  }

  public static async bulkEditAnnotation(ids: number[], approve: boolean) {
    const path = `admin/annotations`
    let response
    if (approve) {
      response = await Api._axiosInstance.put(path, ids, {
        params: Api.projectParams
      })
    } else {
      response = await Api._axiosInstance.delete(path, {
        data: ids,
        params: Api.projectParams
      })
    }
    if (response.status === 200) useAdminStore().setAnnotation(response.data)
  }

  public static async getTaxonomySuggestion() {
    if (!Api.projectParams) return
    const response = await Api._axiosInstance.get(`admin/taxonomysuggestion`, {
      params: Api.projectParams
    })
    if (response.status === 200) useAdminStore().setTaxonomySuggestion(response.data)
  }

  public static async getGlobalTaxonomy() {
    const response = await Api._axiosInstance.get(`admin/globaltaxonomy`)
    if (response.status === 200) useAdminStore().setGlobalTaxonomy(response.data)
  }

  public static async renameGlobalTaxonomyLabel(id: number, name: string) {
    await Api._axiosInstance.put(`admin/globaltaxonomy/${id}`, { name })
  }

  public static async getGlobalTaxonomyStatistics() {
    const response = await Api._axiosInstance.get(`admin/globaltaxonomy/statistics`)
    if (response.status === 200) useAdminStore().setGlobalTaxonomyStats(response.data)
  }

  public static async mergeGlobalTaxonomyLabels(sourceIds: number[], targetId: number) {
    await Api._axiosInstance.post(`admin/globaltaxonomy/merge`, {
      source_ids: sourceIds,
      target_id: targetId
    })
    await Api.getGlobalTaxonomy()
  }

  public static async getTaxonomyGroups() {
    const response = await Api._axiosInstance.get(`admin/taxonomygroup`)
    if (response.status === 200) useAdminStore().setTaxonomyGroups(response.data)
  }

  public static async createTaxonomyGroup(name: string, description?: string, parent_id?: number) {
    await Api._axiosInstance.post(`admin/taxonomygroup`, { name, description, parent_id })
    await Api.getTaxonomyGroups()
  }

  public static async updateTaxonomyGroup(
    id: number,
    payload: { name?: string; description?: string; parent_id?: number | null }
  ) {
    await Api._axiosInstance.put(`admin/taxonomygroup/${id}`, payload)
    await Api.getTaxonomyGroups()
  }

  public static async deleteTaxonomyGroup(id: number) {
    await Api._axiosInstance.delete(`admin/taxonomygroup/${id}`)
    await Api.getTaxonomyGroups()
  }

  public static async addLabelsToGroup(groupId: number, labelIds: number[]) {
    await Api._axiosInstance.post(`admin/taxonomygroup/${groupId}/labels`, { label_ids: labelIds })
    await Api.getTaxonomyGroups()
  }

  public static async removeLabelFromGroup(groupId: number, labelId: number) {
    await Api._axiosInstance.delete(`admin/taxonomygroup/${groupId}/labels`, {
      params: { label_id: labelId }
    })
    await Api.getTaxonomyGroups()
  }

  public static async putTaxonomySuggestion(id: number, dbRef: string, value: any) {
    const response = await Api._axiosInstance.put(
      `admin/taxonomysuggestion/${id}/${dbRef}`,
      {
        data: value
      },
      {
        params: Api.projectParams
      }
    )
    if (response.status === 200) useAdminStore().setTaxonomy(response.data)
  }

  public static async postTaxonomySuggestion(value: string) {
    const response = await Api._axiosInstance.post(
      `admin/taxonomysuggestion`,
      {
        name: value
      },
      {
        params: Api.projectParams
      }
    )
    if (response.status === 200) useAdminStore().setTaxonomySuggestion(response.data)
  }

  public static async deleteTaxonomySuggestion(id: number) {
    await Api._axiosInstance.delete(`admin/taxonomysuggestion/${id}`, { params: Api.projectParams })
  }

  public static async downloadAnnotated() {
    const response = await Api._axiosInstance.get(`admin/annotated`, {
      params: Api.projectParams,
      responseType: 'blob'
    })
    if (response.status === 200) {
      const blob = new Blob([response.data], { type: 'text/csv' }) // Create a Blob object
      const url = URL.createObjectURL(blob) // Create download URL

      const link = document.createElement('a') // Create temporary <a> element
      link.href = url // Set href attribute to download URL
      link.download = `annotated_data_${Api._projectName}.csv` // Set file name

      document.body.appendChild(link) // Append <a> element to document body
      link.click() // Trigger click event on <a> element

      document.body.removeChild(link) // Remove <a> element from document body
    }
  }

  public static async uploadFilesForAnnotation(files: File[]) {
    const formData = new FormData()
    for (const file of files) formData.append('files[]', file)

    const response = await Api._axiosInstance.post(`admin/annotated`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      },
      params: Api.projectParams
    })
    if (response.status === 200) useAdminStore().setAnnotation(response.data)
  }

  public static async getModels() {
    if (!Api.projectParams) return
    const response = await Api._axiosInstance.get(`admin/mlmodel`, { params: Api.projectParams })
    if (response.status === 200) useAdminStore().setModels(response.data)
  }

  public static async postModel(model_name: string, file_uuids: string[]) {
    const response = await Api._axiosInstance.post(
      `admin/mlmodel`,
      {
        data: {
          model_name: model_name,
          file_uuids: file_uuids
        }
      },
      {
        params: Api.projectParams
      }
    )
    if (response.status === 200) useAdminStore().setModels(response.data)
  }

  public static async getFiles(
    class_ids?: string[],
    project_ids?: string[],
    resolved?: boolean,
    limit = 100,
    offset = 0,
    append = false
  ) {
    const response = await Api._axiosInstance.get(`admin/file`, {
      params: {
        class_ids: class_ids?.join(','),
        project_ids: project_ids?.join(','),
        resolved: resolved,
        limit,
        offset
      }
    })
    if (response.status === 200) useAdminStore().setFiles(response.data, append)
  }

  public static async getUsers() {
    const response = await Api._axiosInstance.get(`admin/user`)
    if (response.status === 200) useAdminStore().setUsers(response.data)
  }

  public static async postUser(username: string, projectIds?: Array<number | string>) {
    const payload: { username: string; project_ids?: number[] } = {
      username: username
    }
    if (projectIds && projectIds.length) {
      payload.project_ids = projectIds.map((id) => Number(id)).filter((id) => !Number.isNaN(id))
    }
    const response = await Api._axiosInstance.post(`admin/user`, {
      ...payload
    })
    if (response.status === 200) useAdminStore().setUsers(response.data)
  }

  public static async putUser(username: string, dbRef: string, value: any) {
    await Api._axiosInstance.put(`admin/user/${username}/${dbRef}`, {
      data: value
    })
  }

  public static async deleteUser(username: string) {
    const response = await Api._axiosInstance.delete(`admin/user/${username}`)
    if (response.status === 200) useAdminStore().setUsers(response.data)
  }

  public static async getOrigin() {
    const response = await Api._axiosInstance.get(`admin/origin`)
    if (response.status === 200) useAdminStore().setOrigin(response.data)
  }

  // ----------------------- Training Orchestration -----------------------
  public static async startTraining(
    max_evals?: number,
    evaluate: boolean = false,
    model_type: string = 'DCASE'
  ) {
    const body: any = {
      model_type: model_type,
      evaluate: evaluate
    }
    if (max_evals !== undefined) body.max_evals = max_evals
    const response = await Api._axiosInstance.post(`admin/train/start`, body, {
      params: Api.projectParams
    })
    return response.data as { task_id: string; status: string }
  }

  public static async cancelTraining(task_id: string) {
    const response = await Api._axiosInstance.post(`admin/train/cancel/${task_id}`)
    return response.data as any
  }

  public static async listTrainingTasks() {
    const response = await Api._axiosInstance.get(`admin/train/list`)
    return response.data as {
      tasks: Array<{
        task_id: string
        status: string
        origin: string
        project_name: string
        model_type: string
        started_at: number
        ended_at?: number
        model_name?: string
        registered?: boolean
      }>
    }
  }

  public static async registerTrainedModel(task_id: string) {
    const response = await Api._axiosInstance.post(`admin/train/register/${task_id}`)
    return response.data as {
      task_id: string
      model_name: string
      project_id: number
      ml_model_id: number
    }
  }

  public static async previewCrossProjectDataset(payload: {
    project_ids?: number[]
    origins?: string[]
    group_ids: number[]
    resolved_only?: boolean
    from_date?: string
    to_date?: string
    limit_per_class?: number
  }) {
    const response = await Api._axiosInstance.post(`admin/train/dataset-preview`, payload)
    return response.data
  }

  public static async createCrossProjectModel(payload: {
    file_uuids: string[]
    target_project_id: number
    model_type?: string
    test_path?: string
  }) {
    const response = await Api._axiosInstance.post(`admin/train/cross-project`, payload)
    if (response.status === 200) useAdminStore().setModels([response.data])
    return response.data
  }
}
